import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api, { getApiErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useExpenditureLabels } from '../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../components/TypeAheadLookup';
import { toast } from 'sonner';
import {
  FileQuestion, FileText, Gavel, Info, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Save, Send, Check,
  Clock, Users, ClipboardList, Settings2, File, BarChart3, Eye, Plus, Trash2, Upload, Download,
  X, Search, GripVertical, Star, Hash, Calendar, ToggleLeft, MessageSquare, Paperclip
} from 'lucide-react';

const STEPS = [
  { id: 'type', label: 'Type', icon: FileQuestion },
  { id: 'details', label: 'Details', icon: FileText },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'suppliers', label: 'Suppliers', icon: Users },
  { id: 'requirements', label: 'Requirements', icon: ClipboardList },
  { id: 'response', label: 'Response Setup', icon: Settings2 },
  { id: 'documents', label: 'Documents', icon: File },
  { id: 'evaluation', label: 'Evaluation', icon: BarChart3 },
  { id: 'review', label: 'Review', icon: Eye },
];

const TYPE_INFO = {
  RFI: { icon: Info, color: 'border-blue-400 bg-blue-50', badge: 'bg-blue-100 text-blue-700',
    title: 'Request for Information', desc: 'Gather supplier information, capabilities, and interest',
    use: 'Use when you need to understand the market before issuing a formal solicitation' },
  RFQ: { icon: FileText, color: 'border-violet-400 bg-violet-50', badge: 'bg-violet-100 text-violet-700',
    title: 'Request for Quotation', desc: 'Collect pricing against clearly defined requirements',
    use: 'Use when requirements are clear and price is the primary differentiator' },
  RFP: { icon: FileText, color: 'border-amber-400 bg-amber-50', badge: 'bg-amber-100 text-amber-700',
    title: 'Request for Proposal', desc: 'Evaluate technical, commercial, and qualitative proposals',
    use: 'Use for complex procurements requiring weighted multi-criteria evaluation' },
  REVERSE_AUCTION: { icon: Gavel, color: 'border-rose-400 bg-rose-50', badge: 'bg-rose-100 text-rose-700',
    title: 'Reverse Auction', desc: 'Run a live supplier price-down event',
    use: 'Use for competitive real-time bidding on well-specified commodities or services' },
};

const Q_TYPES = [
  { value: 'yes_no', label: 'Yes / No' }, { value: 'single_select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' }, { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' }, { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' }, { value: 'file_upload', label: 'File Upload' },
];

export default function SourcingEventWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [eventRef, setEventRef] = useState(searchParams.get('ref') || '');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!searchParams.get('ref'));
  const autoSaveTimer = useRef(null);

  const [form, setForm] = useState({
    type: '', title: '', description: '', category: '', currency: 'USD',
    expense_code_1: '', expense_code_2: '', expense_code_3: '', template: '',
    source_mode: 'standalone', source_event_ref: '',
    // Timeline
    publish_date: '', question_start_date: '', question_deadline: '',
    response_deadline: '', evaluation_start_date: '', intended_award_date: '',
    closing_date: '', timezone: 'America/Los_Angeles',
    auto_close: true, allow_late: false,
    reminder_settings: { days_7: true, days_3: true, hours_24: true, hours_1: false },
    // Suppliers
    visibility_settings: { mode: 'private' },
    invited_suppliers: [],
    qualification_requirements: { must_be_approved: true, must_accept_nda: false, must_accept_terms: true },
    // Requirements
    requirement_summary: '', delivery_location: '', incoterms: '', tax_treatment: '',
    partial_responses_allowed: false, alternate_offers_allowed: false, substitutions_allowed: false,
    line_items: [], lots: [],
    service_requirements: { scope: '', deliverables: '', milestones: '', certifications: '' },
    // Response Setup - RFI
    sections: [],
    // Response Setup - RFQ
    pricing_setup: { bid_basis: 'unit_price', taxes_included: false, freight_included: false,
      currency_locked: true, allow_partial: false, price_validity: '', lead_time_required: true },
    // Response Setup - RFP
    scoring_model: [],
    // Response Setup - Auction
    auction_settings: { minimum_decrement: 0, decrement_type: 'fixed', auto_extend: true,
      extension_minutes: 5, max_extensions: 10, bid_must_beat: 'current_best' },
    bidding_structure: 'multi_line', bid_visibility: 'rank_only',
    // Documents
    documents: [], supplier_instructions: '', internal_notes: '',
    supplier_acknowledgements: { accept_terms: false, accept_confidentiality: false, certify_accuracy: false },
    // Evaluation
    evaluation_method: '', evaluators: [], award_strategy: 'single_supplier',
    evaluation_weights: { price: 70, delivery: 15, quality: 10, esg: 5 },
  });

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const fNested = (parent, key, val) => setForm(p => ({ ...p, [parent]: { ...p[parent], [key]: val } }));

  // Load existing event if editing
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      api.get(`/api/rfx/events/${ref}`).then(r => {
        setForm(p => ({ ...p, ...r.data }));
        setEventRef(ref);
        if (r.data.wizard_step != null) setStep(r.data.wizard_step);
      }).catch(() => toast.error('Failed to load event')).finally(() => setLoading(false));
    }
  }, [searchParams]);

  // Autosave every 30 seconds
  useEffect(() => {
    if (!eventRef) return;
    autoSaveTimer.current = setInterval(async () => {
      try {
        await api.post(`/api/rfx/events/${eventRef}/autosave`, { ...form, wizard_step: step });
      } catch {}
    }, 30000);
    return () => clearInterval(autoSaveTimer.current);
  }, [eventRef, form, step]);

  // Create event on first "Next" from Type step
  const createEvent = async () => {
    if (!form.type) { toast.error('Select an event type'); return false; }
    setSaving(true);
    try {
      const r = await api.post('/api/rfx/events', {
        type: form.type, title: form.title || `New ${form.type}`, status: 'DRAFT',
        source_mode: form.source_mode, wizard_step: 1,
      });
      setEventRef(r.data.event_ref);
      toast.success(`${r.data.event_ref} created as draft`);
      setSaving(false);
      return true;
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); setSaving(false); return false; }
  };

  const saveStep = async () => {
    if (!eventRef) return;
    try {
      await api.post(`/api/rfx/events/${eventRef}/autosave`, { ...form, wizard_step: step });
    } catch {}
  };

  const handleNext = async () => {
    if (step === 0 && !eventRef) {
      const ok = await createEvent();
      if (!ok) return;
    } else {
      await saveStep();
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => { saveStep(); setStep(s => Math.max(s - 1, 0)); };

  const handleSaveDraft = async () => {
    setSaving(true);
    if (!eventRef) { await createEvent(); } else { await saveStep(); }
    toast.success('Draft saved');
    setSaving(false);
  };

  const handlePublish = async () => {
    if (!eventRef) return;
    await saveStep();
    try {
      const v = await api.post(`/api/rfx/events/${eventRef}/validate`);
      if (v.data.errors?.length) {
        v.data.errors.forEach(e => toast.error(e));
        return;
      }
      v.data.warnings?.forEach(w => toast.warning(w));
      await api.post(`/api/rfx/events/${eventRef}/publish`);
      toast.success('Event published!');
      navigate('/rfq');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Publish failed')); }
  };

  // Completeness calculation
  const completeness = (() => {
    let filled = 0, total = 8;
    if (form.type) filled++;
    if (form.title) filled++;
    if (form.description) filled++;
    if (form.closing_date || form.response_deadline) filled++;
    if (form.invited_suppliers?.length) filled++;
    if (form.line_items?.length || form.sections?.length) filled++;
    if (form.documents?.length) filled++;
    if (form.evaluation_method) filled++;
    return Math.round(filled / total * 100);
  })();

  if (loading) return <AppShell><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div></AppShell>;

  return (
    <AppShell>
      <div className="flex flex-col h-[calc(100vh-56px)]">
        {/* Stepper */}
        <div className="flex-shrink-0 bg-white border-b px-6 py-3">
          <div className="flex items-center gap-1 max-w-6xl mx-auto">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <React.Fragment key={s.id}>
                  <button onClick={() => (eventRef || i === 0) && setStep(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isActive ? 'bg-indigo-600 text-white' :
                      isDone ? 'bg-indigo-50 text-indigo-700' : 'text-slate-400 hover:text-slate-600'
                    }`} data-testid={`step-${s.id}`}>
                    {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    <span className="hidden lg:inline">{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Main content + sidebar */}
        <div className="flex-1 flex overflow-hidden">
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl">
              {step === 0 && <StepType form={form} f={f} />}
              {step === 1 && <StepDetails form={form} f={f} />}
              {step === 2 && <StepTimeline form={form} f={f} fNested={fNested} />}
              {step === 3 && <StepSuppliers form={form} f={f} fNested={fNested} eventRef={eventRef} />}
              {step === 4 && <StepRequirements form={form} f={f} />}
              {step === 5 && <StepResponseSetup form={form} f={f} fNested={fNested} eventRef={eventRef} />}
              {step === 6 && <StepDocuments form={form} f={f} eventRef={eventRef} />}
              {step === 7 && <StepEvaluation form={form} f={f} fNested={fNested} />}
              {step === 8 && <StepReview form={form} eventRef={eventRef} />}
            </div>
          </div>

          {/* Sticky Summary Panel */}
          <div className="w-72 flex-shrink-0 bg-slate-50 border-l p-4 overflow-y-auto hidden xl:block">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Event Summary</h3>
            <div className="space-y-3">
              {eventRef && <div><span className="text-[10px] text-slate-400">Reference</span><p className="text-sm font-mono font-bold text-indigo-700">{eventRef}</p></div>}
              <div><span className="text-[10px] text-slate-400">Type</span><p className="text-sm">{form.type ? (TYPE_INFO[form.type]?.title || form.type) : '—'}</p></div>
              <div><span className="text-[10px] text-slate-400">Title</span><p className="text-sm font-medium truncate">{form.title || '—'}</p></div>
              <div><span className="text-[10px] text-slate-400">Status</span><Badge className="text-[9px] bg-slate-200 text-slate-600">Draft</Badge></div>
              <div><span className="text-[10px] text-slate-400">Owner</span><p className="text-xs">{user?.email || '—'}</p></div>
              <hr />
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-400">Suppliers</span><p className="font-bold">{form.invited_suppliers?.length || 0}</p></div>
                <div><span className="text-slate-400">Items</span><p className="font-bold">{form.line_items?.length || 0}</p></div>
                <div><span className="text-slate-400">Lots</span><p className="font-bold">{form.lots?.length || 0}</p></div>
                <div><span className="text-slate-400">Documents</span><p className="font-bold">{form.documents?.length || 0}</p></div>
              </div>
              <hr />
              <div><span className="text-[10px] text-slate-400">Completeness</span>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${completeness >= 80 ? 'bg-emerald-500' : completeness >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${completeness}%` }} />
                  </div>
                  <span className="text-xs font-bold">{completeness}%</span>
                </div>
              </div>
              {(form.closing_date || form.response_deadline) && (
                <div><span className="text-[10px] text-slate-400">Deadline</span><p className="text-xs">{form.response_deadline || form.closing_date}</p></div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex-shrink-0 bg-white border-t px-6 py-3">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/rfq')}>
                <X className="w-3.5 h-3.5 mr-1" /> Exit
              </Button>
              {step > 0 && <Button variant="outline" size="sm" onClick={handleBack}>
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={saving}>
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              {step < STEPS.length - 1 ? (
                <Button size="sm" onClick={handleNext} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              ) : (
                <Button size="sm" onClick={handlePublish} className="bg-emerald-600 hover:bg-emerald-700">
                  <Send className="w-3.5 h-3.5 mr-1" /> Publish
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 1 — TYPE
   ══════════════════════════════════════════════════════════════════════════════ */

function StepType({ form, f }) {
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Select Event Type</h2>
      <p className="text-sm text-slate-500 mb-6">Choose the type of sourcing event you want to create.</p>

      <div className="grid grid-cols-2 gap-4 mb-8">
        {Object.entries(TYPE_INFO).map(([type, info]) => {
          const Icon = info.icon;
          const selected = form.type === type;
          return (
            <button key={type} onClick={() => f('type', type)}
              className={`border-2 rounded-xl p-5 text-left transition-all ${selected ? `${info.color} ring-2 ring-offset-2 ring-indigo-500` : 'border-slate-200 hover:border-slate-300 hover:shadow-md'}`}
              data-testid={`type-${type}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-5 h-5" />
                <Badge className={`text-xs ${info.badge}`}>{info.title}</Badge>
              </div>
              <p className="text-sm text-slate-700 mb-1">{info.desc}</p>
              <p className="text-xs text-slate-400">{info.use}</p>
            </button>
          );
        })}
      </div>

      {/* Source Mode */}
      <div className="border rounded-lg p-4">
        <Label className="font-semibold text-sm">Source Mode</Label>
        <div className="flex gap-4 mt-2">
          {[{ v: 'standalone', l: 'Standalone Event', d: 'Create from scratch' },
            { v: 'derived', l: 'Derived from Existing', d: 'Copy from a prior sourcing event' }
          ].map(opt => (
            <label key={opt.v} className={`flex-1 border rounded-lg p-3 cursor-pointer transition-all ${form.source_mode === opt.v ? 'border-indigo-400 bg-indigo-50' : 'hover:border-slate-300'}`}>
              <div className="flex items-center gap-2">
                <input type="radio" checked={form.source_mode === opt.v} onChange={() => f('source_mode', opt.v)} className="h-4 w-4" />
                <div><p className="text-sm font-medium">{opt.l}</p><p className="text-xs text-slate-500">{opt.d}</p></div>
              </div>
            </label>
          ))}
        </div>
        {form.source_mode === 'derived' && (
          <div className="mt-3">
            <Label className="text-xs">Source Event Reference</Label>
            <Input value={form.source_event_ref || ''} onChange={e => f('source_event_ref', e.target.value)} placeholder="e.g. RFP-0028" className="mt-1" />
          </div>
        )}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 2 — DETAILS
   ══════════════════════════════════════════════════════════════════════════════ */

function ExpenseCodeFields({ form, f }) {
  const { labels } = useExpenditureLabels();
  return (
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>{labels.exp_code_1 || 'Expense Code 1'}</Label>
        <TypeAheadLookup endpoint="/api/account-codes/1" valueField="code" labelField="description"
          value={form.expense_code_1 || ''} displayValue={form.expense_code_1 || ''}
          placeholder={`Search ${labels.exp_code_1 || 'Expense Code 1'}...`}
          onChange={code => f('expense_code_1', code)}
          data-testid="rfx-expense-code-1" />
      </div>
      <div>
        <Label>{labels.exp_code_2 || 'Expense Code 2'}</Label>
        <TypeAheadLookup endpoint="/api/account-codes/2" valueField="code" labelField="description"
          value={form.expense_code_2 || ''} displayValue={form.expense_code_2 || ''}
          placeholder={`Search ${labels.exp_code_2 || 'Expense Code 2'}...`}
          onChange={code => f('expense_code_2', code)}
          data-testid="rfx-expense-code-2" />
      </div>
      <div>
        <Label>{labels.exp_code_3 || 'Expense Code 3'}</Label>
        <TypeAheadLookup endpoint="/api/account-codes/3" valueField="code" labelField="description"
          value={form.expense_code_3 || ''} displayValue={form.expense_code_3 || ''}
          placeholder={`Search ${labels.exp_code_3 || 'Expense Code 3'}...`}
          onChange={code => f('expense_code_3', code)}
          data-testid="rfx-expense-code-3" />
      </div>
    </div>
  );
}

function StepDetails({ form, f }) {
  const helper = { RFI: 'Use this to gather information, not pricing', RFQ: 'Use this when requirements are clear and price is primary',
    RFP: 'Use this for complex evaluation with technical/commercial responses', REVERSE_AUCTION: 'Use this for live competitive pricing with invited suppliers' };
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Event Details</h2>
      <p className="text-sm text-slate-500 mb-4">{helper[form.type] || 'Define the identity and ownership of this event.'}</p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Event Title *</Label><Input value={form.title || ''} onChange={e => f('title', e.target.value)} placeholder="e.g. Annual IT Hardware Supply 2026" /></div>
          <div className="col-span-2"><Label>Description / Scope</Label><Textarea value={form.description || ''} onChange={e => f('description', e.target.value)} rows={3} placeholder="Describe the requirements, scope, and expectations..." /></div>
          <div><Label>Category / Commodity</Label>
            <TypeAheadLookup endpoint="/api/data/categories" valueField="code" labelField="description"
              value={form.category || ''} displayValue={form.category || ''}
              placeholder="Search categories..." onChange={code => f('category', code)}
              data-testid="rfx-category" />
          </div>
          <div><Label>Currency</Label><Select value={form.currency || 'USD'} onValueChange={v => f('currency', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['USD','GBP','EUR','CAD','AUD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <ExpenseCodeFields form={form} f={f} />
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 3 — TIMELINE
   ══════════════════════════════════════════════════════════════════════════════ */

function StepTimeline({ form, f, fNested }) {
  const isAuction = form.type === 'REVERSE_AUCTION';
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Timeline</h2>
      <p className="text-sm text-slate-500 mb-4">Set the dates and phases for this event.</p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Publish Date</Label><Input type="datetime-local" value={form.publish_date || ''} onChange={e => f('publish_date', e.target.value)} /></div>
          <div><Label>Supplier Question Start</Label><Input type="datetime-local" value={form.question_start_date || ''} onChange={e => f('question_start_date', e.target.value)} /></div>
          <div><Label>Supplier Question Deadline</Label><Input type="datetime-local" value={form.question_deadline || ''} onChange={e => f('question_deadline', e.target.value)} /></div>
          <div><Label>Response Submission Deadline *</Label><Input type="datetime-local" value={form.response_deadline || form.closing_date || ''} onChange={e => { f('response_deadline', e.target.value); f('closing_date', e.target.value); }} /></div>
          <div><Label>Evaluation Start Date</Label><Input type="datetime-local" value={form.evaluation_start_date || ''} onChange={e => f('evaluation_start_date', e.target.value)} /></div>
          <div><Label>Intended Award Date</Label><Input type="datetime-local" value={form.intended_award_date || ''} onChange={e => f('intended_award_date', e.target.value)} /></div>
        </div>
        {isAuction && (
          <div className="border rounded-lg p-4 bg-rose-50">
            <h3 className="text-sm font-semibold mb-3">Auction Timing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Live Auction Start</Label><Input type="datetime-local" value={form.start_date || ''} onChange={e => f('start_date', e.target.value)} /></div>
              <div><Label>Live Auction End</Label><Input type="datetime-local" value={form.end_date || ''} onChange={e => f('end_date', e.target.value)} /></div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2"><Checkbox checked={form.auto_close} onCheckedChange={v => f('auto_close', v)} /><Label>Auto-close at deadline</Label></div>
          <div className="flex items-center gap-2"><Checkbox checked={form.allow_late} onCheckedChange={v => f('allow_late', v)} /><Label>Allow late responses</Label></div>
        </div>
        <div className="border rounded-lg p-4">
          <Label className="font-semibold text-sm">Reminder Notifications</Label>
          <div className="flex flex-wrap gap-4 mt-2">
            {[{ k: 'days_7', l: '7 days before' }, { k: 'days_3', l: '3 days before' }, { k: 'hours_24', l: '24 hours before' }, { k: 'hours_1', l: '1 hour before' }].map(r => (
              <label key={r.k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.reminder_settings?.[r.k] || false} onChange={e => fNested('reminder_settings', r.k, e.target.checked)} className="h-4 w-4" />{r.l}</label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 4 — SUPPLIERS
   ══════════════════════════════════════════════════════════════════════════════ */

function StepSuppliers({ form, f, fNested, eventRef }) {
  const invited = form.invited_suppliers || [];
  const [resolvedNames, setResolvedNames] = useState({});

  // Resolve supplier codes to names for any string entries
  useEffect(() => {
    const codes = invited.filter(s => typeof s === 'string').filter(c => !resolvedNames[c]);
    if (codes.length === 0) return;
    const resolve = async () => {
      const names = { ...resolvedNames };
      for (const code of codes) {
        try {
          const r = await api.get('/api/data/suppliers', { params: { search: code, page_size: 1 } });
          const list = r.data.data || r.data || [];
          const match = list.find(s => s.supplier_code === code);
          names[code] = match ? (match.supplier_name || match.name || code) : code;
        } catch { names[code] = code; }
      }
      setResolvedNames(names);
    };
    resolve();
  }, [invited]); // eslint-disable-line

  const addSupplier = (code, name) => {
    if (!code) return;
    if (invited.some(s => (typeof s === 'string' ? s : s.supplier_code) === code)) return;
    f('invited_suppliers', [...invited, { supplier_code: code, supplier_name: name }]);
  };

  const removeSupplier = (code) => {
    f('invited_suppliers', invited.filter(s => (typeof s === 'string' ? s : s.supplier_code) !== code));
  };

  const getCode = (s) => typeof s === 'string' ? s : s.supplier_code;
  const getName = (s) => {
    if (typeof s === 'string') return resolvedNames[s] || s;
    return s.supplier_name || s.supplier_code;
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Suppliers</h2>
      <p className="text-sm text-slate-500 mb-4">Choose participating suppliers and define access rules.</p>

      {/* Visibility */}
      <div className="border rounded-lg p-4 mb-4">
        <Label className="font-semibold text-sm">Event Visibility</Label>
        <div className="flex gap-3 mt-2">
          {[{ v: 'private', l: 'Private / Invited Only' }, { v: 'public_login', l: 'Public, Login Required' }, { v: 'public_open', l: 'Public Open Event' }].map(opt => (
            <label key={opt.v} className={`flex-1 border rounded-lg p-3 cursor-pointer text-center text-sm ${form.visibility_settings?.mode === opt.v ? 'border-indigo-400 bg-indigo-50 font-semibold' : 'hover:border-slate-300'}`}>
              <input type="radio" className="sr-only" checked={form.visibility_settings?.mode === opt.v} onChange={() => fNested('visibility_settings', 'mode', opt.v)} />
              {opt.l}
            </label>
          ))}
        </div>
      </div>

      {/* Search & Add — TypeAhead */}
      <div className="mb-4">
        <Label>Search Suppliers to Invite</Label>
        <div className="mt-1">
          <TypeAheadLookup
            endpoint="/api/data/suppliers"
            searchParam="search"
            valueField="supplier_code"
            labelField="supplier_name"
            secondaryField="supplier_code"
            placeholder="Type supplier name or code..."
            value=""
            displayValue=""
            clearOnSelect
            onChange={(code, record) => {
              if (code && record) addSupplier(code, record.supplier_name || record.name || code);
            }}
            excludeValues={invited.map(s => getCode(s))}
            data-testid="supplier-search-typeahead"
          />
        </div>
      </div>

      {/* Invited List — shows names */}
      <div className="border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-2">Invited Suppliers ({invited.length})</h3>
        {invited.length === 0 ? <p className="text-xs text-slate-400">No suppliers invited yet.</p> : (
          <div className="flex flex-wrap gap-2">
            {invited.map(s => (
              <Badge key={getCode(s)} className="bg-indigo-50 text-indigo-700 text-xs gap-1 pr-1 py-1">
                {getName(s)} <span className="text-slate-400 font-normal">({getCode(s)})</span>
                <button onClick={() => removeSupplier(getCode(s))} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 5 — REQUIREMENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function StepRequirements({ form, f }) {
  const [reqTab, setReqTab] = useState('overview');
  const itemFileRef = useRef(null);
  const lotFileRef = useRef(null);
  const [itemDragOver, setItemDragOver] = useState(false);
  const [lotDragOver, setLotDragOver] = useState(false);

  const addItem = () => f('line_items', [...(form.line_items || []), { id: `li_${Date.now()}`, description: '', quantity: 1, uom: 'EA', baseline_price: 0, mandatory: true }]);
  const updateItem = (idx, key, val) => { const u = [...(form.line_items || [])]; u[idx] = { ...u[idx], [key]: val }; f('line_items', u); };
  const removeItem = (idx) => f('line_items', (form.line_items || []).filter((_, i) => i !== idx));
  const addLot = () => f('lots', [...(form.lots || []), { id: `lot_${Date.now()}`, name: `Lot ${(form.lots || []).length + 1}`, description: '', items: [], award_basis: 'lowest_price' }]);

  // ── Shared CSV parser ──
  const parseCSV = (text) => {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
      const parts = line.match(/(".*?"|[^,]+)/g);
      return (parts || []).map(p => p.replace(/^"|"$/g, '').trim());
    });
  };

  // ── CSV/XLSX Import for Line Items ──
  const downloadItemTemplate = () => {
    const csv = 'Description,Quantity,UOM,Baseline Price\n"Example widget",100,EA,25.50\n"Service fee",1,LOT,5000';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'line_items_template.csv'; a.click();
  };

  const processItemFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const rows = parseCSV(evt.target.result);
        if (rows.length < 2) { toast.error('File must have a header row and at least one data row'); return; }
        // Build header map for flexible column matching
        const headers = rows[0].map(h => h.toLowerCase().trim());
        const colIdx = (names) => headers.findIndex(h => names.some(n => h.includes(n)));
        const iDesc = colIdx(['description', 'desc', 'item']);
        const iQty = colIdx(['quantity', 'qty']);
        const iUom = colIdx(['uom', 'unit']);
        const iPrice = colIdx(['price', 'cost', 'baseline', 'estimated']);
        const imported = [];
        for (let i = 1; i < rows.length; i++) {
          const r = rows[i];
          const desc = (iDesc >= 0 ? r[iDesc] : r[0]) || '';
          const qty = parseFloat(iQty >= 0 ? r[iQty] : r[1]) || 1;
          const uom = (iUom >= 0 ? r[iUom] : r[2]) || 'EA';
          const price = parseFloat(iPrice >= 0 ? r[iPrice] : r[3]) || 0;
          if (desc) imported.push({ id: `li_${Date.now()}_${i}`, description: desc, quantity: qty, uom, baseline_price: price, mandatory: true });
        }
        if (imported.length === 0) { toast.error('No valid items found in file'); return; }
        f('line_items', [...(form.line_items || []), ...imported]);
        toast.success(`Imported ${imported.length} line item(s)`);
      } catch { toast.error('Failed to parse file'); }
    };
    reader.readAsText(file);
  };

  const handleItemImport = (e) => { processItemFile(e.target.files?.[0]); e.target.value = ''; };
  const handleItemDrop = (e) => { e.preventDefault(); setItemDragOver(false); const file = e.dataTransfer.files?.[0]; if (file) processItemFile(file); };

  // ── CSV/XLSX Import for Lots ──
  const downloadLotTemplate = () => {
    const csv = 'Lot Name,Description,Award Basis\n"Lot 1","Office supplies","lowest_price"\n"Lot 2","IT equipment","best_value"';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'lots_template.csv'; a.click();
  };

  const processLotFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const rows = parseCSV(evt.target.result);
        if (rows.length < 2) { toast.error('File must have a header row and at least one data row'); return; }
        const imported = [];
        for (let i = 1; i < rows.length; i++) {
          const r = rows[i];
          const name = r[0] || '';
          const desc = r[1] || '';
          const award = r[2] || 'lowest_price';
          if (name) imported.push({ id: `lot_${Date.now()}_${i}`, name, description: desc, items: [], award_basis: award });
        }
        if (imported.length === 0) { toast.error('No valid lots found in file'); return; }
        f('lots', [...(form.lots || []), ...imported]);
        toast.success(`Imported ${imported.length} lot(s)`);
      } catch { toast.error('Failed to parse file'); }
    };
    reader.readAsText(file);
  };

  const handleLotImport = (e) => { processLotFile(e.target.files?.[0]); e.target.value = ''; };
  const handleLotDrop = (e) => { e.preventDefault(); setLotDragOver(false); const file = e.dataTransfer.files?.[0]; if (file) processLotFile(file); };

  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Requirements</h2>
      <p className="text-sm text-slate-500 mb-4">Define what is being sourced.</p>
      <div className="flex gap-2 mb-4 border-b">
        {['overview', 'items', 'lots', 'services'].map(t => (
          <button key={t} onClick={() => setReqTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 capitalize ${reqTab === t ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{t === 'items' ? 'Line Items' : t === 'services' ? 'Service Requirements' : t}</button>
        ))}
      </div>
      {reqTab === 'overview' && (
        <div className="space-y-4">
          <div><Label>Requirement Summary</Label><Textarea value={form.requirement_summary || ''} onChange={e => f('requirement_summary', e.target.value)} rows={3} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Delivery Location</Label><Input value={form.delivery_location || ''} onChange={e => f('delivery_location', e.target.value)} /></div>
            <div><Label>Incoterms</Label><Input value={form.incoterms || ''} onChange={e => f('incoterms', e.target.value)} placeholder="e.g. FOB, CIF, DDP" /></div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.partial_responses_allowed} onChange={e => f('partial_responses_allowed', e.target.checked)} className="h-4 w-4" />Partial responses allowed</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.alternate_offers_allowed} onChange={e => f('alternate_offers_allowed', e.target.checked)} className="h-4 w-4" />Alternate offers allowed</label>
          </div>
        </div>
      )}
      {reqTab === 'items' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-slate-500">{(form.line_items || []).length} item(s)</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={downloadItemTemplate} data-testid="download-items-template">
                <Download className="w-3.5 h-3.5 mr-1" /> Template
              </Button>
              <Button size="sm" variant="outline" onClick={() => itemFileRef.current?.click()} data-testid="import-items-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> Import CSV
              </Button>
              <input ref={itemFileRef} type="file" accept=".csv,.txt" onChange={handleItemImport} className="hidden" />
              <Button size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Item</Button>
            </div>
          </div>
          {/* Drag-and-drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setItemDragOver(true); }}
            onDragLeave={() => setItemDragOver(false)}
            onDrop={handleItemDrop}
            className={`border-2 rounded-lg transition-colors ${itemDragOver ? 'border-indigo-400 bg-indigo-50/50 border-dashed' : 'border-transparent'}`}
            data-testid="items-drop-zone">
            {(form.line_items || []).length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center"
                onClick={() => itemFileRef.current?.click()}>
                <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-medium">Drag & drop a CSV file here, or click to browse</p>
                <p className="text-xs text-slate-400 mt-1">Download the template first for the correct format</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b"><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 w-20">Qty</th><th className="px-3 py-2 w-20">UOM</th><th className="px-3 py-2 w-28">Baseline $</th><th className="px-3 py-2 w-10" /></tr></thead>
                  <tbody>
                    {(form.line_items || []).map((item, idx) => (
                      <tr key={item.id || idx} className="border-b">
                        <td className="px-3 py-1 text-sm text-slate-400">{idx + 1}</td>
                        <td className="px-3 py-1"><Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="h-7 text-sm border-0 bg-transparent" placeholder="Item description" /></td>
                        <td className="px-3 py-1"><Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 1)} className="h-7 text-sm w-16" /></td>
                        <td className="px-3 py-1"><Input value={item.uom} onChange={e => updateItem(idx, 'uom', e.target.value)} className="h-7 text-sm w-16" /></td>
                        <td className="px-3 py-1"><Input type="number" value={item.baseline_price || ''} onChange={e => updateItem(idx, 'baseline_price', parseFloat(e.target.value) || 0)} className="h-7 text-sm" placeholder="0.00" /></td>
                        <td className="px-3 py-1"><button onClick={() => removeItem(idx)} className="text-red-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {itemDragOver && (
              <div className="text-center py-2 text-xs text-indigo-600 font-medium">Drop CSV file to import line items</div>
            )}
          </div>
        </div>
      )}
      {reqTab === 'lots' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-slate-500">{(form.lots || []).length} lot(s)</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={downloadLotTemplate} data-testid="download-lots-template">
                <Download className="w-3.5 h-3.5 mr-1" /> Template
              </Button>
              <Button size="sm" variant="outline" onClick={() => lotFileRef.current?.click()} data-testid="import-lots-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> Import CSV
              </Button>
              <input ref={lotFileRef} type="file" accept=".csv,.txt" onChange={handleLotImport} className="hidden" />
              <Button size="sm" onClick={addLot} data-testid="add-lot-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Lot</Button>
            </div>
          </div>
          {/* Drag-and-drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setLotDragOver(true); }}
            onDragLeave={() => setLotDragOver(false)}
            onDrop={handleLotDrop}
            className={`border-2 rounded-lg transition-colors ${lotDragOver ? 'border-indigo-400 bg-indigo-50/50 border-dashed' : 'border-transparent'}`}
            data-testid="lots-drop-zone">
            {(form.lots || []).length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center"
                onClick={() => lotFileRef.current?.click()}>
                <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-medium">Drag & drop a CSV file here, or click to browse</p>
                <p className="text-xs text-slate-400 mt-1">Download the template first for the correct format</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(form.lots || []).map((lot, idx) => (
                  <div key={lot.id || idx} className="border rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Lot Name</Label><Input value={lot.name} onChange={e => { const u = [...form.lots]; u[idx] = { ...u[idx], name: e.target.value }; f('lots', u); }} className="h-8 text-sm" /></div>
                      <div><Label className="text-xs">Description</Label><Input value={lot.description || ''} onChange={e => { const u = [...form.lots]; u[idx] = { ...u[idx], description: e.target.value }; f('lots', u); }} className="h-8 text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {lotDragOver && (
              <div className="text-center py-2 text-xs text-indigo-600 font-medium">Drop CSV file to import lots</div>
            )}
          </div>
        </div>
      )}
      {reqTab === 'services' && (
        <div className="space-y-4">
          <div><Label>Scope of Work</Label><Textarea value={form.service_requirements?.scope || ''} onChange={e => f('service_requirements', { ...form.service_requirements, scope: e.target.value })} rows={3} /></div>
          <div><Label>Deliverables</Label><Textarea value={form.service_requirements?.deliverables || ''} onChange={e => f('service_requirements', { ...form.service_requirements, deliverables: e.target.value })} rows={2} /></div>
          <div><Label>Certifications Required</Label><Input value={form.service_requirements?.certifications || ''} onChange={e => f('service_requirements', { ...form.service_requirements, certifications: e.target.value })} /></div>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 6 — RESPONSE SETUP (Dynamic by type)
   ══════════════════════════════════════════════════════════════════════════════ */

function StepResponseSetup({ form, f, fNested, eventRef }) {
  const type = form.type;
  const subtitle = { RFI: 'Questionnaire Setup', RFQ: 'Pricing & Commercial Questions', RFP: 'Proposal, Scoring & Questionnaire', REVERSE_AUCTION: 'Auction Rules & Questions' };
  const contextHint = {
    RFI: 'Build a questionnaire for suppliers to respond to. This is the primary response mechanism for RFIs.',
    RFQ: 'Configure pricing rules below, then optionally add commercial questions for suppliers.',
    RFP: 'Configure scoring criteria, then build a detailed questionnaire with weighted scoring.',
    REVERSE_AUCTION: 'Configure auction rules below, then optionally add questions to assess supplier capability and compliance before they participate in the auction.',
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Response Setup</h2>
      <p className="text-sm text-indigo-600 font-medium mb-4">{subtitle[type] || 'Configure how suppliers will respond'}</p>

      {/* Type-specific configuration */}
      {type === 'RFQ' && <RFQResponseSetup form={form} f={f} fNested={fNested} />}
      {type === 'RFP' && <RFPResponseSetup form={form} f={f} />}
      {type === 'REVERSE_AUCTION' && <AuctionResponseSetup form={form} f={f} fNested={fNested} />}

      {/* Unified Questionnaire — available for ALL event types */}
      <div className="mt-6 border-t pt-4">
        <h3 className="text-base font-bold mb-1">
          {type === 'RFI' ? 'Questionnaire' : type === 'RFP' ? 'Detailed Questionnaire' : type === 'RFQ' ? 'Commercial Questions (Optional)' : 'Questions (Optional)'}
        </h3>
        <p className="text-xs text-slate-500 mb-3">{contextHint[type]}</p>
        <UnifiedQuestionnaire sections={form.sections || []} setSections={s => f('sections', s)} eventRef={eventRef} showWeights={type === 'RFP'} />
      </div>
    </div>
  );
}

function UnifiedQuestionnaire({ sections, setSections, eventRef, showWeights }) {
  const [importMode, setImportMode] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const addSection = () => setSections([...sections, { section_name: `Section ${sections.length + 1}`, questions: [] }]);
  const updateSection = (idx, field, value) => { const u = [...sections]; u[idx] = { ...u[idx], [field]: value }; setSections(u); };
  const removeSection = (idx) => { if (!window.confirm('Remove this section and all its questions?')) return; setSections(sections.filter((_, i) => i !== idx)); };
  const addQ = (sIdx) => { const u = [...sections]; u[sIdx].questions.push({ question_id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`, text: '', type: 'free_text', options: [], weight: 1, max_score: 10, required: true }); setSections(u); };
  const updateQ = (sIdx, qIdx, field, value) => { const u = [...sections]; u[sIdx].questions[qIdx] = { ...u[sIdx].questions[qIdx], [field]: value }; setSections(u); };
  const removeQ = (sIdx, qIdx) => { const u = [...sections]; u[sIdx].questions.splice(qIdx, 1); setSections(u); };
  const moveQ = (sIdx, qIdx, dir) => { const u = [...sections]; const qs = u[sIdx].questions; const n = qIdx + dir; if (n < 0 || n >= qs.length) return; [qs[qIdx], qs[n]] = [qs[n], qs[qIdx]]; setSections(u); };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      processFile(file);
    } else if (file) {
      toast.error('Please drop an Excel (.xlsx, .xls) or CSV file');
    }
  };

  const processFile = async (file) => {
    setImportLoading(true);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      setImportData(rows);
      setImportMode(true);
      toast.success(`${rows.length} row(s) parsed from ${file.name}`);
    } catch (err) { toast.error('Failed to parse spreadsheet: ' + err.message); }
    setImportLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    e.target.value = '';
  };

  const confirmImport = async (mode) => {
    if (!importData?.length) return;
    if (eventRef) {
      try {
        const r = await api.post(`/api/rfx/events/${eventRef}/import-questions`, { rows: importData, mode });
        toast.success(r.data.message);
        const ev = await api.get(`/api/rfx/events/${eventRef}`);
        setSections(ev.data.sections || []);
        toast.success('Questionnaire saved');
      } catch (e) { toast.error(getApiErrorMessage(e, 'Import failed')); }
    } else {
      const sectionsMap = {};
      for (const row of importData) {
        const secName = row.Section || row.section || 'General';
        if (!sectionsMap[secName]) sectionsMap[secName] = { section_name: secName, questions: [] };
        const qText = row.Question || row.question || '';
        if (!qText) continue;
        let qType = (row.Type || row.type || 'free_text').toLowerCase().replace(/\s/g, '_');
        const validTypes = ['yes_no', 'multiple_choice', 'free_text', 'numeric', 'file_upload', 'date', 'rating'];
        if (!validTypes.includes(qType)) qType = 'free_text';
        const optsRaw = row.Options || row.options || row['Options (comma-separated)'] || '';
        const sep = optsRaw.includes('|') ? '|' : ',';
        const options = qType === 'multiple_choice' ? optsRaw.split(sep).map(o => o.trim()).filter(Boolean) : [];
        sectionsMap[secName].questions.push({
          question_id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          text: qText, type: qType, options,
          weight: parseFloat(row.Weight || row.weight || 1) || 1,
          max_score: parseFloat(row['Max Score'] || row.max_score || 10) || 10,
          required: ['yes', 'true', '1', 'y'].includes(String(row.Required || row.required || 'yes').toLowerCase()),
        });
      }
      const newSections = Object.values(sectionsMap);
      const merged = mode === 'replace' ? newSections : [...sections, ...newSections];
      setSections(merged);
      // Force immediate save to prevent auto-save race condition
      if (eventRef) {
        try { await api.post(`/api/rfx/events/${eventRef}/autosave`, { sections: merged }); } catch {}
      }
      toast.success(`${importData.length} question(s) imported and saved`);
    }
    setImportData(null);
    setImportMode(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/api/rfx/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'rfx_questions_template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{sections.length} section(s), {totalQuestions} question(s)</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportMode(!importMode)}
            className={importMode ? 'bg-indigo-50 border-indigo-300' : ''} data-testid="import-toggle-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import
          </Button>
          <Button size="sm" onClick={addSection} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-section-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Section
          </Button>
        </div>
      </div>

      {/* Import Panel — Drag & Drop */}
      {importMode && (
        <div
          onDrop={handleFileDrop}
          onDragOver={e => e.preventDefault()}
          className="border-2 border-dashed border-indigo-300 rounded-lg p-4 bg-indigo-50/50"
        >
          <h4 className="font-medium text-sm mb-2">Import Questions from Spreadsheet</h4>
          {!importData && (
            <>
              <div
                onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.accept = '.xlsx,.xls,.csv'; i.onchange = handleFileUpload; i.click(); }}
                className="border-2 border-dashed border-indigo-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-100/30 transition-all mb-2"
              >
                <Upload className="w-6 h-6 mx-auto mb-2 text-indigo-400" />
                <p className="text-sm font-medium text-slate-700">Drag & drop Excel or CSV file here</p>
                <p className="text-xs text-slate-400 mt-1">or click to browse — Columns: Section, Question, Type, Options, Weight, Max Score, Required</p>
              </div>
              {importLoading && <p className="text-sm text-indigo-600">Parsing...</p>}
            </>
          )}
          {importData && (
            <div className="mt-1">
              <p className="text-sm font-medium text-emerald-700 mb-2">{importData.length} row(s) ready to import</p>
              <div className="max-h-48 overflow-y-auto border rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{Object.keys(importData[0] || {}).map(k => <th key={k} className="px-2 py-1 text-left font-medium">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t">{Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 truncate max-w-[200px]">{String(v)}</td>)}</tr>
                    ))}
                    {importData.length > 10 && <tr><td colSpan={99} className="px-2 py-1 text-slate-400">...and {importData.length - 10} more rows</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={() => confirmImport('append')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="import-append-btn">Append to Existing</Button>
                <Button size="sm" variant="outline" onClick={() => confirmImport('replace')} data-testid="import-replace-btn">Replace All</Button>
                <Button size="sm" variant="ghost" onClick={() => { setImportData(null); setImportMode(false); }}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sections & Questions */}
      {sections.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <FileQuestion className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No questions yet. Add a section or import from a spreadsheet.</p>
        </div>
      ) : (
        sections.map((sec, sIdx) => (
          <div key={sIdx} className="border rounded-lg overflow-hidden" data-testid={`section-${sIdx}`}>
            <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1">
                <Input value={sec.section_name} onChange={e => updateSection(sIdx, 'section_name', e.target.value)}
                  className="h-7 text-sm font-semibold bg-transparent border-none focus:bg-white max-w-[300px]" data-testid={`section-name-${sIdx}`} />
                <span className="text-xs text-slate-400">{sec.questions.length} question(s)</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => addQ(sIdx)} className="h-7 px-2 text-indigo-600" data-testid={`add-question-${sIdx}`}><Plus className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => removeSection(sIdx)} className="h-7 px-2 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="p-3 space-y-2">
              {sec.questions.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No questions. <button onClick={() => addQ(sIdx)} className="text-indigo-600 underline">Add one</button></p>
              ) : sec.questions.map((q, qIdx) => (
                <div key={q.question_id || qIdx} className="border rounded p-2.5 bg-white hover:shadow-sm" data-testid={`question-${sIdx}-${qIdx}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-0.5 mt-1">
                      <button onClick={() => moveQ(sIdx, qIdx, -1)} disabled={qIdx === 0} className="text-slate-300 hover:text-slate-500 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveQ(sIdx, qIdx, 1)} disabled={qIdx === sec.questions.length - 1} className="text-slate-300 hover:text-slate-500 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <span className="text-xs text-slate-400 font-mono mt-2 w-5">Q{qIdx + 1}</span>
                    <div className="flex-1 space-y-1.5">
                      <Input value={q.text} onChange={e => updateQ(sIdx, qIdx, 'text', e.target.value)}
                        placeholder="Enter your question..." className="text-sm h-8" data-testid={`question-text-${sIdx}-${qIdx}`} />
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={q.type} onValueChange={v => updateQ(sIdx, qIdx, 'type', v)}>
                          <SelectTrigger className="w-[150px] h-7 text-xs" data-testid={`question-type-${sIdx}-${qIdx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{Q_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {showWeights && (
                          <>
                            <div className="flex items-center gap-1"><Label className="text-xs">Weight:</Label>
                              <Input type="number" value={q.weight} onChange={e => updateQ(sIdx, qIdx, 'weight', parseFloat(e.target.value) || 1)} className="w-14 h-7 text-xs" /></div>
                            <div className="flex items-center gap-1"><Label className="text-xs">Max:</Label>
                              <Input type="number" value={q.max_score} onChange={e => updateQ(sIdx, qIdx, 'max_score', parseFloat(e.target.value) || 10)} className="w-14 h-7 text-xs" /></div>
                          </>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Checkbox checked={q.required} onCheckedChange={c => updateQ(sIdx, qIdx, 'required', c)} data-testid={`question-required-${sIdx}-${qIdx}`} />
                          <Label className="text-xs">Required</Label>
                        </div>
                      </div>
                      {q.type === 'multiple_choice' && (
                        <div className="ml-2 space-y-1">
                          <Label className="text-xs text-slate-500">Options:</Label>
                          {(q.options || []).map((opt, oIdx) => (
                            <div key={oIdx} className="flex items-center gap-1">
                              <Checkbox disabled className="opacity-40" />
                              <Input value={opt} onChange={e => { const u = [...(q.options || [])]; u[oIdx] = e.target.value; updateQ(sIdx, qIdx, 'options', u); }} placeholder={`Option ${oIdx + 1}`} className="h-7 text-xs flex-1" />
                              <button onClick={() => updateQ(sIdx, qIdx, 'options', (q.options || []).filter((_, i) => i !== oIdx))} className="text-red-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                          <Button size="sm" variant="ghost" onClick={() => updateQ(sIdx, qIdx, 'options', [...(q.options || []), ''])} className="h-6 text-xs text-indigo-600"><Plus className="w-3 h-3 mr-1" /> Add Option</Button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeQ(sIdx, qIdx)} className="text-red-300 hover:text-red-500 p-1"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function RFQResponseSetup({ form, f, fNested }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Bid Basis</Label><Select value={form.pricing_setup?.bid_basis || 'unit_price'} onValueChange={v => fNested('pricing_setup', 'bid_basis', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="unit_price">Unit Price</SelectItem><SelectItem value="total_line">Total Line Value</SelectItem>
          <SelectItem value="lot_total">Lot Total</SelectItem><SelectItem value="basket_total">Basket Total</SelectItem>
        </SelectContent></Select></div>
        <div><Label>Price Validity Period</Label><Input value={form.pricing_setup?.price_validity || ''} onChange={e => fNested('pricing_setup', 'price_validity', e.target.value)} placeholder="e.g. 90 days" /></div>
      </div>
      <div className="flex flex-wrap gap-4">
        {[{ k: 'taxes_included', l: 'Taxes Included' }, { k: 'freight_included', l: 'Freight Included' }, { k: 'currency_locked', l: 'Currency Locked' },
          { k: 'allow_partial', l: 'Allow Partial Pricing' }, { k: 'lead_time_required', l: 'Lead Time Required' }].map(opt => (
          <label key={opt.k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pricing_setup?.[opt.k] || false}
            onChange={e => fNested('pricing_setup', opt.k, e.target.checked)} className="h-4 w-4" />{opt.l}</label>
        ))}
      </div>
    </div>
  );
}

function RFPResponseSetup({ form, f }) {
  const addCriterion = () => f('scoring_model', [...(form.scoring_model || []), { criterion: '', weight: 0, scored_by: '', mandatory: true }]);
  const updateCriterion = (idx, key, val) => { const u = [...(form.scoring_model || [])]; u[idx] = { ...u[idx], [key]: val }; f('scoring_model', u); };
  const totalWeight = (form.scoring_model || []).reduce((s, c) => s + (parseFloat(c.weight) || 0), 0);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" defaultChecked />Technical proposal upload required</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" defaultChecked />Commercial proposal upload required</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" defaultChecked />Pricing response required</label>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="font-semibold">Scoring Model</Label>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold ${Math.abs(totalWeight - 100) < 0.1 ? 'text-emerald-600' : 'text-red-600'}`}>{totalWeight}%</span>
            <Button size="sm" variant="outline" onClick={addCriterion}><Plus className="w-3 h-3 mr-1" /> Add</Button>
          </div>
        </div>
        <table className="w-full text-sm border rounded-lg overflow-hidden">
          <thead><tr className="bg-slate-50 border-b"><th className="px-3 py-2 text-left">Criterion</th><th className="px-3 py-2 w-20">Weight %</th><th className="px-3 py-2 w-32">Scored By</th><th className="px-3 py-2 w-10" /></tr></thead>
          <tbody>{(form.scoring_model || []).map((c, idx) => (
            <tr key={idx} className="border-b">
              <td className="px-3 py-1"><Input value={c.criterion} onChange={e => updateCriterion(idx, 'criterion', e.target.value)} className="h-7 text-xs border-0" placeholder="e.g. Technical Fit" /></td>
              <td className="px-3 py-1"><Input type="number" value={c.weight} onChange={e => updateCriterion(idx, 'weight', parseFloat(e.target.value) || 0)} className="h-7 text-xs w-16" /></td>
              <td className="px-3 py-1"><Input value={c.scored_by || ''} onChange={e => updateCriterion(idx, 'scored_by', e.target.value)} className="h-7 text-xs" placeholder="Evaluator" /></td>
              <td className="px-3 py-1"><button onClick={() => f('scoring_model', (form.scoring_model || []).filter((_, i) => i !== idx))} className="text-red-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function AuctionResponseSetup({ form, f, fNested }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Bidding Structure</Label><Select value={form.bidding_structure || 'multi_line'} onValueChange={v => f('bidding_structure', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="single_item">Item Only</SelectItem><SelectItem value="multi_line">Multi-Line Item</SelectItem>
          <SelectItem value="lot_based">Lot Only</SelectItem><SelectItem value="total_basket">Total Basket</SelectItem>
        </SelectContent></Select></div>
        <div><Label>Bid Visibility</Label><Select value={form.bid_visibility || 'rank_only'} onValueChange={v => f('bid_visibility', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="own_only">Own Bid Only</SelectItem><SelectItem value="rank_only">Rank Only</SelectItem>
          <SelectItem value="best_anonymous">Best Bid (Anonymous)</SelectItem><SelectItem value="open">Full Leaderboard</SelectItem>
          <SelectItem value="sealed">Sealed (Hidden Until Close)</SelectItem>
        </SelectContent></Select></div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><Label>Min. Decrement</Label><Input type="number" value={form.auction_settings?.minimum_decrement || ''} onChange={e => fNested('auction_settings', 'minimum_decrement', parseFloat(e.target.value) || 0)} placeholder="e.g. 50" /></div>
        <div><Label>Decrement Type</Label><Select value={form.auction_settings?.decrement_type || 'fixed'} onValueChange={v => fNested('auction_settings', 'decrement_type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="fixed">Fixed Amount</SelectItem><SelectItem value="percentage">Percentage</SelectItem>
        </SelectContent></Select></div>
        <div><Label>Bid Must Beat</Label><Select value={form.auction_settings?.bid_must_beat || 'current_best'} onValueChange={v => fNested('auction_settings', 'bid_must_beat', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="own_prior">Own Prior Bid</SelectItem><SelectItem value="current_best">Current Best</SelectItem>
          <SelectItem value="current_best_by_min">Best + Min Decrement</SelectItem>
        </SelectContent></Select></div>
      </div>
      <div className="border rounded-lg p-4">
        <Label className="font-semibold text-sm mb-2 block">Anti-Sniping / Extension Rules</Label>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2"><Checkbox checked={form.auction_settings?.auto_extend} onCheckedChange={v => fNested('auction_settings', 'auto_extend', v)} /><Label>Auto-extend on late bids</Label></div>
          <div><Label>Extend by (mins)</Label><Input type="number" value={form.auction_settings?.extension_minutes || 5} onChange={e => fNested('auction_settings', 'extension_minutes', parseInt(e.target.value) || 5)} /></div>
          <div><Label>Max extensions</Label><Input type="number" value={form.auction_settings?.max_extensions || 10} onChange={e => fNested('auction_settings', 'max_extensions', parseInt(e.target.value) || 10)} /></div>
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 7 — DOCUMENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function StepDocuments({ form, f, eventRef }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!eventRef) { toast.error('Save the event first'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('visibility', 'public');
      await api.post(`/api/rfx/events/${eventRef}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await api.get(`/api/rfx/events/${eventRef}`);
      f('documents', r.data.documents || []);
      toast.success(`${file.name} uploaded`);
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Documents</h2>
      <p className="text-sm text-slate-500 mb-4">Upload files, add instructions, and set acknowledgements.</p>

      {/* Upload */}
      <div onDrop={e => { e.preventDefault(); if (e.dataTransfer.files?.[0]) upload(e.dataTransfer.files[0]); }} onDragOver={e => e.preventDefault()}
        onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.onchange = ev => { if (ev.target.files?.[0]) upload(ev.target.files[0]); }; i.click(); }}
        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-slate-50 transition-all mb-4">
        <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
        <p className="text-sm">{uploading ? 'Uploading...' : 'Drag & drop or click to upload'}</p>
      </div>

      {(form.documents || []).length > 0 && (
        <div className="border rounded-lg divide-y mb-4">
          {form.documents.map((d, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2 text-sm"><span>{d.file_name}</span><Badge className="text-[9px]">{d.visibility || 'public'}</Badge></div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <div><Label className="font-semibold">Supplier Instructions</Label><Textarea value={form.supplier_instructions || ''} onChange={e => f('supplier_instructions', e.target.value)} rows={4} placeholder="How to respond, required format, deadlines, contact method..." /></div>
        <div><Label className="font-semibold">Internal Notes (not visible to suppliers)</Label><Textarea value={form.internal_notes || ''} onChange={e => f('internal_notes', e.target.value)} rows={3} placeholder="Evaluation concerns, strategy, supplier notes..." /></div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 8 — EVALUATION
   ══════════════════════════════════════════════════════════════════════════════ */

function StepEvaluation({ form, f, fNested }) {
  const addEvaluator = () => f('evaluators', [...(form.evaluators || []), { name: '', role: '', section: '', voting: true }]);
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Evaluation</h2>
      <p className="text-sm text-slate-500 mb-4">Set evaluation method, evaluators, and award approach.</p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Evaluation Method</Label><Select value={form.evaluation_method || ''} onValueChange={v => f('evaluation_method', v)}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>
            <SelectItem value="informational">Informational Only</SelectItem>
            <SelectItem value="lowest_price">Lowest Compliant Price</SelectItem>
            <SelectItem value="weighted_score">Weighted Score</SelectItem>
            <SelectItem value="buyer_discretion">Buyer Discretion</SelectItem>
            <SelectItem value="auction_result">Auction Result (subject to approval)</SelectItem>
          </SelectContent></Select></div>
          <div><Label>Award Strategy</Label><Select value={form.award_strategy || 'single_supplier'} onValueChange={v => f('award_strategy', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="single_supplier">Single Supplier</SelectItem>
            <SelectItem value="split_item">Split by Item</SelectItem>
            <SelectItem value="split_lot">Split by Lot</SelectItem>
            <SelectItem value="shortlist">Shortlist for Next Round</SelectItem>
            <SelectItem value="convert_auction">Convert to Auction</SelectItem>
          </SelectContent></Select></div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2"><Label className="font-semibold">Evaluators</Label>
            <Button size="sm" variant="outline" onClick={addEvaluator}><Plus className="w-3 h-3 mr-1" /> Add</Button></div>
          {(form.evaluators || []).length === 0 ? <p className="text-xs text-slate-400">No evaluators assigned.</p> : (
            <table className="w-full text-sm border rounded-lg overflow-hidden">
              <thead><tr className="bg-slate-50 border-b"><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Section</th><th className="px-3 py-2 w-20">Voting</th><th className="px-3 py-2 w-10" /></tr></thead>
              <tbody>{(form.evaluators || []).map((ev, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-1"><Input value={ev.name} onChange={e => { const u = [...form.evaluators]; u[idx] = { ...u[idx], name: e.target.value }; f('evaluators', u); }} className="h-7 text-xs border-0" /></td>
                  <td className="px-3 py-1"><Input value={ev.role || ''} onChange={e => { const u = [...form.evaluators]; u[idx] = { ...u[idx], role: e.target.value }; f('evaluators', u); }} className="h-7 text-xs border-0" /></td>
                  <td className="px-3 py-1"><Input value={ev.section || ''} onChange={e => { const u = [...form.evaluators]; u[idx] = { ...u[idx], section: e.target.value }; f('evaluators', u); }} className="h-7 text-xs border-0" /></td>
                  <td className="px-3 py-1 text-center"><input type="checkbox" checked={ev.voting} onChange={e => { const u = [...form.evaluators]; u[idx] = { ...u[idx], voting: e.target.checked }; f('evaluators', u); }} /></td>
                  <td className="px-3 py-1"><button onClick={() => f('evaluators', form.evaluators.filter((_, i) => i !== idx))} className="text-red-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   STEP 9 — REVIEW
   ══════════════════════════════════════════════════════════════════════════════ */

function StepReview({ form, eventRef }) {
  const [validation, setValidation] = useState(null);
  useEffect(() => {
    if (eventRef) api.post(`/api/rfx/events/${eventRef}/validate`).then(r => setValidation(r.data)).catch(() => {});
  }, [eventRef]);

  const Section = ({ title, children }) => (
    <div className="border rounded-lg mb-3"><div className="bg-slate-50 px-4 py-2 font-semibold text-sm border-b">{title}</div><div className="p-4 text-sm space-y-1">{children}</div></div>
  );
  const Row = ({ label, value }) => <div className="flex"><span className="text-slate-500 w-48 flex-shrink-0">{label}</span><span className="font-medium">{value || '—'}</span></div>;

  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Review & Publish</h2>
      <p className="text-sm text-slate-500 mb-4">Review your event before publishing.</p>

      {validation && (validation.errors?.length > 0 || validation.warnings?.length > 0) && (
        <div className="mb-4 space-y-2">
          {validation.errors?.map((e, i) => <div key={i} className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">{e}</div>)}
          {validation.warnings?.map((w, i) => <div key={i} className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2 rounded-lg text-sm">{w}</div>)}
        </div>
      )}

      <Section title="Event Summary">
        <Row label="Type" value={TYPE_INFO[form.type]?.title} />
        <Row label="Title" value={form.title} />
        <Row label="Description" value={form.description} />
        <Row label="Category" value={form.category} />
        <Row label="Currency" value={form.currency} />
        {form.expense_code_1 && <Row label="Expense Code 1" value={form.expense_code_1} />}
        {form.expense_code_2 && <Row label="Expense Code 2" value={form.expense_code_2} />}
        {form.expense_code_3 && <Row label="Expense Code 3" value={form.expense_code_3} />}
      </Section>
      <Section title="Timeline">
        <Row label="Response Deadline" value={form.response_deadline || form.closing_date} />
        <Row label="Evaluation Start" value={form.evaluation_start_date} />
        <Row label="Intended Award" value={form.intended_award_date} />
      </Section>
      <Section title="Suppliers">
        <Row label="Visibility" value={form.visibility_settings?.mode} />
        <Row label="Invited Count" value={String((form.invited_suppliers || []).length)} />
      </Section>
      <Section title="Requirements">
        <Row label="Line Items" value={String((form.line_items || []).length)} />
        <Row label="Lots" value={String((form.lots || []).length)} />
      </Section>
      <Section title="Documents">
        <Row label="Attached" value={String((form.documents || []).length)} />
      </Section>
      <Section title="Evaluation">
        <Row label="Method" value={form.evaluation_method} />
        <Row label="Award Strategy" value={form.award_strategy} />
        <Row label="Evaluators" value={String((form.evaluators || []).length)} />
      </Section>
    </div>
  );
}
